import { dynamicUrl } from "@arenko-group/arenko-ui/dist/helpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getBaseUrl } from "@/helpers/fetch.helpers";
import logger from "@/helpers/logger";
import { getJwtToken } from "@/helpers/storage.helpers";

export type CustomerMap = Record<string, string>;

export type ActiveFault = {
  class: string;
  code: number;
  customerId: string;
  data: number;
  deviceId: string;
  deviceName: string;
  message: string;
  occurred: string;
  reset: string;
  severity: string;
};

export type HistoricalFault = {
  class: string;
  code: number;
  customerId: string;
  data: number;
  deviceId: string;
  message: string;
  occurred: string;
  reset: string;
  severity: string;
};

export type FaultMessage =
  | { type: "historical"; data: HistoricalFault[] }
  | { type: "deviceNames"; data: Record<string, string> }
  | { type: "active"; data: ActiveFault[] };

const faultExchangeSockets: Record<string, Promise<WebSocket>> = {};

export const getFaultExchangeSocket = (deviceId: string) => {
  const baseUrl = dynamicUrl("fault-exchange", deviceId);

  if (!(deviceId in faultExchangeSockets)) {
    const promise = new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(baseUrl);

      ws.addEventListener("open", () => {
        resolve(ws);
      });
      ws.addEventListener("error", () => {
        const error = new Error(
          `WebSocket connection error for Fault Exchange device ${deviceId}`
        );
        reject(error);
      });
    });

    faultExchangeSockets[deviceId] = promise;
  }
  return faultExchangeSockets[deviceId];
};

export const faultExchangeApi = createApi({
  reducerPath: "faultExchangeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl("/fault-exchange/"),
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${getJwtToken()}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getCustomerMap: build.query<CustomerMap, void>({
      query: () => "customer-map",
    }),
    getFaultMessages: build.query<FaultMessage[], string>({
      queryFn: () => ({
        data: [],
      }),
      async onCacheEntryAdded(
        deviceId: string | undefined,
        { cacheDataLoaded, cacheEntryRemoved, updateCachedData }
      ) {
        if (!deviceId) return;

        try {
          const ws = await getFaultExchangeSocket(deviceId);
          await cacheDataLoaded;

          ws.onmessage = (event: MessageEvent) => {
            try {
              const message = JSON.parse(event.data);
              updateCachedData((draft) => {
                draft.push(message);
              });
            } catch (error) {
              logger.error(
                "Error parsing fault exchange websocket message",
                error
              );
            }
          };

          ws.onclose = (event) => {
            if (event.code !== 1000) {
              logger.warn(
                `WebSocket closed unexpectedly for device ${deviceId}. Code: ${event.code}, Reason: ${event.reason}`
              );
            }
          };
        } catch (err) {
          logger.error(
            `There was an error while handling fault exchange websocket for device ${deviceId}`,
            err
          );
        }

        await cacheEntryRemoved;
        if (deviceId in faultExchangeSockets) {
          const ws = await faultExchangeSockets[deviceId];
          ws.close();
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete faultExchangeSockets[deviceId];
        }
      },
    }),
  }),
});

export const { useGetCustomerMapQuery, useGetFaultMessagesQuery } =
  faultExchangeApi;
